import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Search, MessageCircle, Package, Truck, MapPinned, Smile, Frown, CreditCard } from "lucide-react";
import { useLang, DIRECTION_LABEL } from "@/i18n";
import { useSettings } from "@/context/SettingsContext";
import { api, apiError } from "@/lib/api";
import { fmtGBP, fmtIDR, fmtDate, fmtDateTime, gbpToIdr } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { StatusTimeline } from "@/components/StatusTimeline";

export default function TrackPage() {
  const { t, lang } = useLang();
  const { settings } = useSettings();
  const [params] = useSearchParams();
  const [ref, setRef] = useState(params.get("ref") || "");
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [booking, setBooking] = useState(null);
  const [fb, setFb] = useState({ satisfied: null, comment: "" });
  const [fbSent, setFbSent] = useState(false);
  const [payLoading, setPayLoading] = useState(false);

  const markPaid = async () => {
    setPayLoading(true);
    try {
      const { data } = await api.post("/bookings/mark-paid", { reference: booking.reference, phone });
      setBooking((b) => ({ ...b, payment_status: data.payment_status }));
      toast.success(t("paid_thanks"));
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally { setPayLoading(false); }
  };

  const lookup = async () => {
    if (!ref.trim() || !phone.trim()) { toast.error(t("err_required")); return; }
    setLoading(true);
    setBooking(null);
    try {
      const { data } = await api.post("/bookings/track", { reference: ref, phone });
      setBooking(data);
      setFbSent(!!data.feedback);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail, t("track_not_found")));
    } finally {
      setLoading(false);
    }
  };

  const submitFeedback = async () => {
    if (fb.satisfied === null) { toast.error(t("err_required")); return; }
    try {
      await api.post("/bookings/feedback", { reference: booking.reference, phone, satisfied: fb.satisfied, comment: fb.comment });
      toast.success(t("feedback_thanks"));
      setFbSent(true);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const trip = booking?.trip;
  const rate = trip?.exchange_rate_gbp_to_idr;
  const customsTotal = (booking?.customs_charges || []).reduce((s, c) => s + (c.amount_idr || 0), 0);

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
      <div className="mb-8">
        <h1 className="text-3xl font-bold tracking-tight font-display">{t("track_title")}</h1>
        <p className="text-muted-foreground mt-1">{t("track_sub")}</p>
      </div>

      <Card className="p-6 grid sm:grid-cols-3 gap-4 items-end">
        <div className="sm:col-span-1 space-y-1.5">
          <Label className="text-sm">{t("ref_number")}</Label>
          <Input data-testid="track-ref-input" value={ref} onChange={(e) => setRef(e.target.value)} placeholder="JST-2026-BHX123" />
        </div>
        <div className="sm:col-span-1 space-y-1.5">
          <Label className="text-sm">{t("phone")}</Label>
          <Input data-testid="track-phone-input" value={phone} onChange={(e) => setPhone(e.target.value)} />
        </div>
        <Button data-testid="track-submit-btn" onClick={lookup} disabled={loading}>
          <Search className="h-4 w-4 mr-1" /> {loading ? t("loading") : t("track_btn")}
        </Button>
      </Card>

      {booking && (
        <div className="mt-8 space-y-6 animate-fade-up" data-testid="track-result">
          <Card className="p-6">
            <div className="flex items-start justify-between flex-wrap gap-3">
              <div>
                <p className="text-xs text-muted-foreground font-mono">{booking.reference}</p>
                <h2 className="text-xl font-bold font-display mt-1">{booking.customer_name}</h2>
                {trip && <p className="text-primary font-medium text-sm mt-1">{trip.title} · {DIRECTION_LABEL[trip.direction]}</p>}
              </div>
              <Badge className="bg-primary/15 text-primary border-0" data-testid="track-payment-badge">
                {t("payment")}: {booking.payment_status === "paid" ? t("paid") : booking.payment_status === "awaiting_verification" ? t("payment_awaiting") : t("unpaid")}
              </Badge>
            </div>
          </Card>

          <div className="grid md:grid-cols-2 gap-6">
            <Card className="p-6">
              <h3 className="font-display font-semibold text-lg mb-4 flex items-center gap-2"><Truck className="h-5 w-5 text-primary" /> {t("timeline")}</h3>
              <StatusTimeline tracking={booking.tracking} />
            </Card>

            <div className="space-y-6">
              <Card className="p-6 space-y-3">
                <h3 className="font-display font-semibold text-lg flex items-center gap-2"><Package className="h-5 w-5 text-primary" /> {t("fee_breakdown")}</h3>
                <Row label={t("weight_est")} value={`${booking.estimated_weight_kg} kg`} />
                {booking.adjusted_weight_kg != null && <Row label={t("weight_adj")} value={`${booking.adjusted_weight_kg} kg`} highlight />}
                <Row label={t("est_fee")} value={fmtGBP(booking.estimated_fee_gbp)} sub={rate ? `≈ ${fmtIDR(gbpToIdr(booking.estimated_fee_gbp, rate))}` : null} />
                {booking.final_fee_gbp != null && (
                  <Row label={t("final_fee")} value={fmtGBP(booking.final_fee_gbp)} sub={rate ? `≈ ${fmtIDR(gbpToIdr(booking.final_fee_gbp, rate))}` : null} highlight />
                )}
                {customsTotal > 0 && (
                  <div className="border-t border-border pt-3 space-y-1">
                    {(booking.customs_charges || []).map((c, i) => (
                      <Row key={i} label={c.description} value={fmtIDR(c.amount_idr)} />
                    ))}
                  </div>
                )}
                {booking.delivery_option === "delivery" && (
                  <div className="border-t border-border pt-3 space-y-1">
                    <Row label={t("domestic_cost")} value={fmtIDR(booking.domestic_cost_idr)} />
                    <Row label={t("awb")} value={booking.domestic_awb || "—"} mono />
                  </div>
                )}
                {rate && <p className="text-xs text-muted-foreground pt-1">{t("exchange_rate_used")}: £1 = {fmtIDR(rate)}</p>}
              </Card>

              {booking.delivery_option === "pickup" && (
                <Card className="p-6 space-y-2">
                  <h3 className="font-display font-semibold text-lg flex items-center gap-2"><MapPinned className="h-5 w-5 text-primary" /> {t("pickup_info")}</h3>
                  <p className="text-sm text-muted-foreground">{trip?.pickup_address}</p>
                  {booking.pickup_ready_datetime && (
                    <p className="text-sm">{t("pickup_ready")}: <span className="font-semibold">{fmtDateTime(booking.pickup_ready_datetime, lang)}</span></p>
                  )}
                </Card>
              )}

              <Card className="p-6 space-y-3" data-testid="payment-card">
                <h3 className="font-display font-semibold text-lg flex items-center gap-2"><CreditCard className="h-5 w-5 text-primary" /> {t("payment_info")}</h3>
                {(settings.payment_methods || []).map((pm) => (
                  <div key={pm.id} className="rounded-lg border border-border p-3 text-sm" data-testid={`track-pm-${pm.id}`}>
                    <p className="font-semibold">{pm.bank}</p>
                    <p className="font-mono text-base">{pm.account_number}</p>
                    <p className="text-muted-foreground">{t("account_name_l")}: {pm.account_name}</p>
                    {pm.note && <p className="text-xs text-muted-foreground mt-0.5">{pm.note}</p>}
                  </div>
                ))}
                <p className="text-xs text-muted-foreground">{t("payment_note")}</p>
                {booking.status === "waiting_list" && (
                  <p className="text-sm text-amber-600 dark:text-amber-400 font-medium" data-testid="track-waiting-note">{t("waiting_notice")}</p>
                )}
                {booking.payment_status === "unpaid" && booking.status !== "waiting_list" && (
                  <Button className="w-full" data-testid="mark-paid-btn" disabled={payLoading} onClick={markPaid}>
                    {payLoading ? t("loading") : t("mark_paid")}
                  </Button>
                )}
                {booking.payment_status === "awaiting_verification" && (
                  <p className="text-sm text-amber-600 dark:text-amber-400 font-medium" data-testid="awaiting-verif">⏳ {t("payment_awaiting")}</p>
                )}
                {booking.payment_status === "paid" && (
                  <p className="text-sm text-primary font-medium">✓ {t("paid")}</p>
                )}
              </Card>

              <a href={`https://wa.me/${settings.contacts.whatsapp_intl}?text=${encodeURIComponent(`Halo Zaki/Aini, saya ingin menanyakan titipan ${booking.reference}`)}`}
                 target="_blank" rel="noreferrer" data-testid="track-whatsapp-btn">
                <Button variant="outline" className="w-full"><MessageCircle className="h-4 w-4 mr-2 text-emerald-500" /> {t("chat_wa")}</Button>
              </a>
            </div>
          </div>

          {/* Feedback */}
          {booking.status === "completed" && (
            <Card className="p-6 space-y-4" data-testid="feedback-section">
              <h3 className="font-display font-semibold text-lg">{t("feedback_title")}</h3>
              {fbSent ? (
                <p className="text-sm text-primary font-medium" data-testid="feedback-done">{t("feedback_given")}</p>
              ) : (
                <>
                  <div className="flex gap-3">
                    <Button variant={fb.satisfied === true ? "default" : "outline"} data-testid="feedback-satisfied"
                      onClick={() => setFb((p) => ({ ...p, satisfied: true }))}>
                      <Smile className="h-4 w-4 mr-2" /> {t("feedback_satisfied")}
                    </Button>
                    <Button variant={fb.satisfied === false ? "destructive" : "outline"} data-testid="feedback-not"
                      onClick={() => setFb((p) => ({ ...p, satisfied: false }))}>
                      <Frown className="h-4 w-4 mr-2" /> {t("feedback_not")}
                    </Button>
                  </div>
                  <Textarea data-testid="feedback-comment" placeholder={t("feedback_comment")} value={fb.comment}
                    onChange={(e) => setFb((p) => ({ ...p, comment: e.target.value }))} />
                  <Button onClick={submitFeedback} data-testid="feedback-submit">{t("feedback_submit")}</Button>
                </>
              )}
            </Card>
          )}
        </div>
      )}
    </div>
  );
}

const Row = ({ label, value, sub, highlight, mono }) => (
  <div className="flex items-start justify-between gap-3 text-sm">
    <span className="text-muted-foreground">{label}</span>
    <span className="text-right">
      <span className={`${highlight ? "font-bold text-primary" : "font-medium"} ${mono ? "font-mono text-xs" : ""}`}>{value}</span>
      {sub && <span className="block text-xs text-muted-foreground font-mono">{sub}</span>}
    </span>
  </div>
);
